import { Phone, Palette, ClipboardList, Sparkles, Check } from 'lucide-react';
import type { City } from '@/data/cities';

const steps = [
  { 
    icon: Phone, 
    title: 'Rencontre & écoute', 
    desc: 'Un premier échange gratuit pour comprendre votre vision, vos envies et votre budget.',
    details: ['Rendez-vous en présentiel ou visio', 'Analyse de vos besoins et contraintes', 'Écoute active, zéro jugement'],
  },
  { 
    icon: Palette, 
    title: 'Conception & proposition', 
    desc: 'Nous élaborons un projet sur-mesure avec devis détaillé et planning prévisionnel.',
    details: ['Mood board et direction artistique', 'Devis transparent sous 48 à 72h', 'Sélection des meilleurs prestataires'],
  },
  { 
    icon: ClipboardList, 
    title: 'Coordination & production', 
    desc: 'Gestion complète des prestataires, logistique, repérages terrain et répétitions.',
    details: ['Coordination de 10-20 prestataires', 'Repérages et répétitions techniques', 'Planning minute par minute'],
  },
  { 
    icon: Sparkles, 
    title: 'Jour J & magie', 
    desc: 'Nous orchestrons chaque détail pour que vous profitiez pleinement.',
    details: ['Supervision totale en coulisse', 'Gestion des imprévus en mode Ninja', 'Vous profitez, on gère tout'],
  },
];

interface Props { city: City }

const VilleMethodologie = ({ city }: Props) => (
  <section className="py-16 md:py-20 px-6 lg:px-8 bg-muted/20">
    <div className="max-w-5xl mx-auto">
      <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2 text-center">Notre méthodologie</p>
      <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-4 text-center">
        Comment nous travaillons {city.prefix} {city.name}
      </h2>
      <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-12 text-sm">
        Une méthodologie éprouvée en 4 étapes pour des événements réussis, sans stress et sur-mesure.
      </p>

      {/* Connected steps */}
      <div className="relative">
        {/* Connecting line - desktop */}
        <div className="hidden lg:block absolute top-10 left-[calc(12.5%+24px)] right-[calc(12.5%+24px)] h-0.5 bg-gradient-to-r from-primary/30 via-primary/50 to-primary/30" />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <div key={i} className="relative text-center p-6 bg-background rounded-2xl border border-border hover:border-primary/20 hover:shadow-md transition-all">
                {/* Step number badge */}
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-xs font-heading font-bold">
                  {i + 1}
                </div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/10 flex items-center justify-center mx-auto mb-4 mt-2">
                  <Icon size={22} className="text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading font-bold text-foreground mb-2 text-sm">{step.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed mb-3">{step.desc}</p>
                <ul className="space-y-1.5 text-left">
                  {step.details.map((d, j) => (
                    <li key={j} className="flex items-start gap-1.5">
                      <Check size={11} className="text-primary mt-0.5 shrink-0" />
                      <span className="text-[11px] text-muted-foreground">{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

export default VilleMethodologie;
